import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import {
  DataGrid,
  GridActionsCellItem,
} from "@mui/x-data-grid";
import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import CampaignIcon from "@mui/icons-material/Campaign";
import toast, { Toaster } from "react-hot-toast";
import Swal from "sweetalert2";
import * as services from "./../../services/saleng";
import AppBreadcrumbs from "./../../components/AppBreadcrumbs";
import { keyframes } from "@mui/system";
import {
  getAdminBranchId,
  getAdminRole,
  isBranchScopedAdminRole,
  isFullAdminRole,
} from "../../auth/adminAccess";

const PRICE_ANNOUNCEMENT_TYPES = {
  HEAD_OFFICE: "HEAD_OFFICE",
  SELF_ANNOUNCE: "SELF_ANNOUNCE",
};

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(255,165,0,.55); transform: scale(1); }
  60% { box-shadow: 0 0 0 12px rgba(255,165,0,0); transform: scale(1.035); }
  100% { box-shadow: 0 0 0 0 rgba(255,165,0,0); transform: scale(1); }
`;

const Branches = () => {
  const navigate = useNavigate();
  const adminRole = getAdminRole();
  const isFullAccess = isFullAdminRole(adminRole);
  const isBranchScoped = isBranchScopedAdminRole(adminRole);
  const canEditBranch = isFullAccess || isBranchScoped;
  const adminBranchId = getAdminBranchId();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adjustments, setAdjustments] = useState([]);
  const [shop, setShop] = useState(null);
  const [shopType, setShopType] = useState("");
  const [savingShopType, setSavingShopType] = useState(false);
  const [editingBranch, setEditingBranch] = useState(null);
  const [editForm, setEditForm] = useState({
    name: "",
    companyName: "",
    taxNumber: "",
    phoneNumber: "",
    address: "",
    image: null,
    imageFile: null,
      usePriceInsurance: false,
      priceAnnouncementType: PRICE_ANNOUNCEMENT_TYPES.HEAD_OFFICE,
  });
  const [savingBranch, setSavingBranch] = useState(false);
  const currentShopType = shop?.shopType ?? shopType;
  const normalizedShopType = String(currentShopType).trim().toUpperCase();
  const isFranchise =
    normalizedShopType === "FRANCHISE" || normalizedShopType === "แฟรนไชส์";

  const handleSaveShopType = async () => {
    const nextLabel = shopType === "FRANCHISE" ? "แฟรนไชส์" : "ร้านเดียว";
    const result = await Swal.fire({
      icon: "warning",
      title: "ยืนยันการเปลี่ยนประเภทร้าน?",
      text: `ต้องการเปลี่ยนเป็น “${nextLabel}” หรือไม่`,
      showCancelButton: true,
      confirmButtonText: "ยืนยัน",
      cancelButtonText: "ยกเลิก",
      confirmButtonColor: "#185519",
      cancelButtonColor: "#d33",
      customClass: { container: "my-swal" },
    });

    if (!result.isConfirmed) return;

    setSavingShopType(true);
    try {
      const response = await services.updateShopType(shopType);
      const updatedShop = response.data.data;
      setShop(updatedShop);
      setShopType(updatedShop.shopType);
      toast.success("บันทึกประเภทร้านสำเร็จ!");
    } catch (error) {
      toast.error("บันทึกประเภทร้านไม่สำเร็จ กรุณาลองอีกครั้ง");
    } finally {
      setSavingShopType(false);
    }
  };

  const handleEditClick = (row) => () => {
    setEditingBranch(row);
    setEditForm({
      name: row.name ?? "",
      companyName: row.companyName ?? "",
      taxNumber: row.taxNumber ?? "",
      phoneNumber: row.phoneNumber ?? "",
      address: row.address ?? "",
      image: row.image ?? null,
      imageFile: null,
      usePriceInsurance: row.usePriceInsurance ?? false,
      priceAnnouncementType:
        row.priceAnnouncementType || PRICE_ANNOUNCEMENT_TYPES.HEAD_OFFICE,
    });
  };

  const handleEditFormChange = (event) => {
    const { name, value } = event.target;
    setEditForm((previous) => {
      if (name === "usePriceInsurance") {
        const usePriceInsurance = value === "true";
        return {
          ...previous,
          usePriceInsurance,
          priceAnnouncementType: usePriceInsurance
            ? PRICE_ANNOUNCEMENT_TYPES.HEAD_OFFICE
            : previous.priceAnnouncementType,
        };
      }

      return { ...previous, [name]: value };
    });
  };

  const handleImageChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setEditForm((previous) => ({
      ...previous,
      image: URL.createObjectURL(file),
      imageFile: file,
    }));
  };

  const handleSaveBranch = async () => {
    if (!editingBranch || !editForm.name.trim()) return;

    setSavingBranch(true);
    try {
      let imageUrl = editForm.image;
      if (editForm.imageFile) {
        const uploadResponse = await services.uploadFile(
          editForm.imageFile,
          "company_logo",
        );
        imageUrl = uploadResponse.data?.file || null;
      }

      const { imageFile, ...branchForm } = editForm;

      await services.updateBranch(editingBranch.branchId, {
        ...branchForm,
        name: branchForm.name.trim(),
        image: imageUrl,
        usePriceInsurance: editForm.usePriceInsurance,
        priceAnnouncementType: editForm.usePriceInsurance
          ? PRICE_ANNOUNCEMENT_TYPES.HEAD_OFFICE
          : editForm.priceAnnouncementType ||
            PRICE_ANNOUNCEMENT_TYPES.HEAD_OFFICE,
      });
      setEditingBranch(null);
      await fetchData();
      toast.success("แก้ไขข้อมูลสาขาสำเร็จ!");
    } catch (error) {
      toast.error("แก้ไขข้อมูลสาขาไม่สำเร็จ!");
      console.error("Error updating branch:", error);
    } finally {
      setSavingBranch(false);
    }
  };

  const handleDeleteClick = (id) => () => {
    const deletedRow = rows.find((row) => row.id === id);

    Swal.fire({
      customClass: { container: "my-swal" },
      title: "ยืนยันที่จะลบ?",
      text: "คุณจะไม่สามารถย้อนกลับสิ่งนี้ได้!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "ยืนยัน",
      cancelButtonText: "ยกเลิก",
    }).then((result) => {
      if (result.isConfirmed) {
        services
          .deleteBranch(deletedRow.branchId)
          .then(() => {
            toast.success("ลบสำเร็จ!");
            Swal.fire({
              customClass: { container: "my-swal" },
              title: "ลบแล้ว!",
              text: deletedRow.name + " ถูกลบแล้ว",
              icon: "success",
              confirmButtonText: "ตกลง",
            });
            setRows((prevRows) =>
              prevRows
                .filter((row) => row.branchId !== deletedRow.branchId)
                .map((item, index) => ({ ...item, id: index + 1 }))
            );
          })
          .catch((error) => {
            toast.error("ลบไม่สำเร็จ!");
            console.error("Error deleting branch:", error);
          });
      }
    });
  };


  const fetchData = async () => {
    try {
      setLoading(true);
      const shopResponse = await services.getShop();
      const shop = shopResponse.data.data;
      setShop(shop);
      setShopType(shop?.shopType ?? "");

      setAdjustments(shop?.adjustments ?? []);

      const visibleBranches = isBranchScoped
        ? (shop?.branches ?? []).filter(
            (item) => Number(item.id) === adminBranchId,
          )
        : shop?.branches ?? [];

      const shopsWithIndex = visibleBranches.map((item, index) => ({
        ...item,
        id: index + 1,
        branchId: item.id,
        usePriceInsurance: item.usePriceInsurance ?? false,
        priceAnnouncementType:
          item.usePriceInsurance
            ? PRICE_ANNOUNCEMENT_TYPES.HEAD_OFFICE
            : item.priceAnnouncementType || PRICE_ANNOUNCEMENT_TYPES.HEAD_OFFICE,
        count: item._count?.users ?? 0,
        needsAnnounceUpdate: item.needsAnnounceUpdate || false,
      }));

      setRows(shopsWithIndex);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const columns = useMemo(
    () => [
      { field: "id", headerName: "ลำดับ", flex: 0, width: 70 },
      {
        field: "name",
        headerName: "ชื่อสาขา",
        flex: 1,
      },
      {
        field: "companyName",
        headerName: "ชื่อบริษัท",
        width: 180,
      },
      {
        field: "isMain",
        headerName: "ประเภทสาขา",
        width: 115,
        sortable: false,
        renderCell: ({ row }) =>
          row.isMain || row.branchCode === "B01" ? (
            <Chip size="small" color="primary" label="สาขาหลัก" />
          ) : (
            "สาขาทั่วไป"
          ),
      },
      {
        field: "usePriceInsurance",
        headerName: "การประกันราคา",
        type: "singleSelect",
        width: 180,
        valueOptions: [
          { value: false, label: "ไม่ใช้" },
          { value: true, label: "ใช้" },
        ],
      },
      {
        field: "priceAnnouncementType",
        headerName: "การประกาศราคา",
        type: "singleSelect",
        width: 190,
        valueOptions: [
          {
            value: PRICE_ANNOUNCEMENT_TYPES.HEAD_OFFICE,
            label: "สำนักงานใหญ่",
          },
          {
            value: PRICE_ANNOUNCEMENT_TYPES.SELF_ANNOUNCE,
            label: "ประกาศเอง",
          },
        ],
      },
      {
        field: "actions",
        headerName: "",
        flex: 0,
        headerAlign: "center",
        align: "center",
        renderCell: ({ id, row }) => {
          if (!canEditBranch) return null;

          const isMainBranch = row.isMain || row.branchCode === "B01";

          const actions = [
            <GridActionsCellItem
              key={`${id}-edit`}
              icon={<EditIcon />}
              label="Edit"
              className="textPrimary"
              onClick={handleEditClick(row)}
              color="inherit"
            />,
          ];

          if (isFullAccess && !isMainBranch) {
            actions.push(
              <GridActionsCellItem
                key={`${id}-delete`}
                icon={<DeleteIcon />}
                label="Delete"
                onClick={handleDeleteClick(id)}
                color="inherit"
              />,
            );
          }

          return actions;
        },
      },
      {
        field: "publish",
        headerName: "",
        width: 170,
        headerAlign: "center",
        align: "center",
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const { branchId, needsAnnounceUpdate } = params.row;
          const hasAdjustForThisBranch = adjustments?.some(
            (a) => a.branchId === branchId
          );

          if (hasAdjustForThisBranch || needsAnnounceUpdate)
            return (
              <Button
                variant="contained"
                startIcon={<CampaignIcon />}
                onClick={async () => {
                  Swal.fire({
                    title: "ยืนยันประกาศราคา?",
                    text: "คุณแน่ใจหรือไม่ที่จะประกาศราคาสำหรับสาขานี้",
                    icon: "warning",
                    showCancelButton: true,
                    confirmButtonColor: "#f59e0b",
                    cancelButtonColor: "#d33",
                    confirmButtonText: "ใช่, ประกาศเลย!",
                    cancelButtonText: "ยกเลิก",
                    customClass: { container: "my-swal" },
                  }).then(async (result) => {
                    if (result.isConfirmed) {
                      try {
                        // ถ้า needsAnnounceUpdate ให้ patch ก่อน
                        if (needsAnnounceUpdate) {
                          await services.patchBranchAnnounce(branchId);
                          toast.success("อัปเดตประกาศปรับราคาหลักแล้ว!");
                        }

                        if (hasAdjustForThisBranch) {
                          // ประกาศราคาปรับ
                          await services.publishProductAdjustments(branchId);
                          toast.success("อัปเดตประกาศปรับราคาย่อยแล้ว!");
                        }

                        Swal.fire({
                          icon: "success",
                          title: "ประกาศสำเร็จ!",
                          text: "อัปเดตราคาสำหรับสาขานี้แล้ว",
                          confirmButtonText: "ตกลง",
                          customClass: { container: "my-swal" },
                        });

                        await fetchData();
                      } catch (e) {
                        Swal.fire({
                          icon: "error",
                          title: "เกิดข้อผิดพลาด!",
                          text: "ไม่สามารถประกาศราคาได้",
                          confirmButtonText: "กลับ",
                          customClass: { container: "my-swal" },
                        });
                        console.error(e);
                      }
                    }
                  });
                }}
                sx={{
                  bgcolor: "warning.main",
                  color: "warning.contrastText",
                  borderColor: "warning.dark",
                  animation: `${pulse} 1.6s infinite ease-in-out`,
                  fontWeight: 700,
                  borderRadius: 2,
                  px: 2,
                  "&:hover": { bgcolor: "warning.dark" },
                }}
              >
                อัปเดตประกาศ
              </Button>
            );
        },
      },
      {
        field: "pricing",
        headerName: "",
        width: 150,
        headerAlign: "center",
        align: "center",
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Button
            variant="outlined"
            onClick={() => navigate(`/branches/pricing/${params.row.branchId}`)}
          >
            จัดการราคา
          </Button>
        ),
      },
    ],
    [adjustments, canEditBranch, navigate]
  );

  return (
    <div
      style={{
        width: "100%",
        minWidth: 0,
        maxWidth: "100%",
        overflowX: "hidden",
      }}
    >
      <AppBreadcrumbs />
      <Toaster position="bottom-left" reverseOrder={false} />
      <Dialog
        open={Boolean(editingBranch)}
        onClose={() => !savingBranch && setEditingBranch(null)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>แก้ไขข้อมูลสาขา</DialogTitle>
        <DialogContent
          dividers
          sx={{ display: "flex", flexDirection: "column", gap: 2 }}
        >
          <TextField
            label="ชื่อสาขา"
            name="name"
            value={editForm.name}
            onChange={handleEditFormChange}
            required
            autoFocus
            disabled={savingBranch}
          />
          <TextField
            label="ชื่อบริษัท"
            name="companyName"
            value={editForm.companyName}
            onChange={handleEditFormChange}
            disabled={savingBranch}
          />
          <TextField
            label="เลขประจำตัวผู้เสียภาษี"
            name="taxNumber"
            value={editForm.taxNumber}
            onChange={handleEditFormChange}
            disabled={savingBranch}
          />
          <TextField
            label="เบอร์โทรศัพท์"
            name="phoneNumber"
            value={editForm.phoneNumber}
            onChange={handleEditFormChange}
            disabled={savingBranch}
          />
          <TextField
            label="ที่อยู่"
            name="address"
            value={editForm.address}
            onChange={handleEditFormChange}
            multiline
            minRows={3}
            disabled={savingBranch}
          />
          <TextField
            select
            label="การประกันราคา"
            name="usePriceInsurance"
            value={String(editForm.usePriceInsurance)}
            onChange={handleEditFormChange}
            disabled={savingBranch}
          >
            <MenuItem value="false">ไม่ใช้</MenuItem>
            <MenuItem value="true">ใช้</MenuItem>
          </TextField>
          <TextField
            select
            label="การประกาศราคา"
            name="priceAnnouncementType"
            value={editForm.priceAnnouncementType}
            onChange={handleEditFormChange}
            disabled={savingBranch || editForm.usePriceInsurance}
            helperText={
              editForm.usePriceInsurance
                ? "เมื่อใช้การประกันราคา ระบบจะใช้ประกาศราคาจากสำนักงานใหญ่"
                : ""
            }
          >
            <MenuItem value={PRICE_ANNOUNCEMENT_TYPES.HEAD_OFFICE}>
              สำนักงานใหญ่
            </MenuItem>
            <MenuItem value={PRICE_ANNOUNCEMENT_TYPES.SELF_ANNOUNCE}>
              ประกาศเอง
            </MenuItem>
          </TextField>
          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              รูปภาพสาขา
            </Typography>
            {editForm.image && (
              <Box
                component="img"
                src={editForm.image}
                alt="ตัวอย่างรูปสาขา"
                sx={{
                  display: "block",
                  width: 140,
                  height: 100,
                  objectFit: "contain",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  mb: 1,
                }}
              />
            )}
            <Button component="label" variant="outlined" disabled={savingBranch}>
              เลือกรูปภาพ
              <input
                hidden
                type="file"
                accept="image/*"
                onChange={handleImageChange}
              />
            </Button>
            {editForm.image && (
              <Button
                sx={{ ml: 1 }}
                color="error"
                onClick={() =>
                  setEditForm((previous) => ({
                    ...previous,
                    image: null,
                    imageFile: null,
                  }))
                }
                disabled={savingBranch}
              >
                ลบรูปภาพ
              </Button>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setEditingBranch(null)}
            disabled={savingBranch}
          >
            ยกเลิก
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveBranch}
            disabled={savingBranch || !editForm.name.trim()}
          >
            {savingBranch ? "กำลังบันทึก..." : "บันทึก"}
          </Button>
        </DialogActions>
      </Dialog>
      {/* <Paper sx={{ p: 2, mt: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          ข้อมูลร้าน{shop?.name ? `: ${shop.name}` : ""}
        </Typography>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
          <TextField
            select
            label="ประเภทร้าน"
            value={shopType}
            onChange={(event) => setShopType(event.target.value)}
            disabled={!isFullAccess || loading || !shop || savingShopType}
            size="small"
            sx={{ minWidth: 220 }}
          >
            <MenuItem value="FRANCHISE">แฟรนไชส์</MenuItem>
            <MenuItem value="OWN_SHOP">ร้านเดี่ยว</MenuItem>
          </TextField>
          <Button
            variant="contained"
            onClick={handleSaveShopType}
            disabled={
              !isFullAccess ||
              loading ||
              !shop ||
              !shopType ||
              savingShopType ||
              shopType === shop.shopType
            }
          >
            {savingShopType ? "กำลังบันทึก..." : "บันทึกประเภทร้าน"}
          </Button>
        </Box>
      </Paper> */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1.5,
          mt: 1,
        }}
      >
        <Typography component="h2" variant="h5">
          รายการสาขา
        </Typography>
        {isFranchise && (
          <Button
            color="success"
            variant="contained"
            onClick={() => navigate("/branches/add")}
          >
            เพิ่มสาขา
          </Button>
        )}
      </Box>

      <Box sx={{ width: "100%", minWidth: 0 }}>
        <Paper
          sx={{
            width: "100%",
            minWidth: 0,
            maxWidth: "100%",
            overflowX: "auto",
          }}
        >
          <Box sx={{ minWidth: 1080 }}>
            <DataGrid
              rows={rows}
              columns={columns}
              autoHeight
              loading={loading}
              slotProps={{
                loadingOverlay: {
                  variant: "skeleton",
                  noRowsVariant: "skeleton",
                },
              }}
              initialState={{ pagination: { page: 0, pageSize: 50 } }}
              pageSizeOptions={[25, 50, 100]}
              sx={{ width: "100%" }}
            />
          </Box>
        </Paper>
      </Box>
    </div>
  );
};

export default Branches;
